import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'
import { INDUSTRIES } from '../../utils/industrySchemas'
import LoadingSpinner from '../common/LoadingSpinner'
import { nsConfirm } from '../shared/ns-confirm'
import '../../styles/admin-pages.css'
import {
  ChevronLeft,
  User,
  MessageSquare,
  Zap,
  Send,
  Edit,
  MoreHorizontal,
  CreditCard,
  Globe,
  FileEdit,
  Clock,
  ArrowRight,
  ExternalLink,
  Check,
  Rocket,
  BarChart3,
  Trash2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'

// ── Status helpers (mirror ClientManager) ──────────────────────────────────────
const STATUS_TONE = { active: 'success', trial: 'warning', inactive: 'danger', suspended: 'danger' }
const STATUS_LABEL = { active: 'Active', trial: 'Trial', inactive: 'Inactive', suspended: 'Suspended' }

function getClientStatus(client) {
  if (!client.is_active) return 'inactive'
  if (client.billing_status === 'trialing') return 'trial'
  return 'active'
}

function gradIndex(id) {
  return ((id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

// published → live, draft → draft, archived → archived
const SITE_STATUS = {
  published: { tone: 'success', label: 'Live' },
  draft: { tone: '', label: 'Draft' },
  archived: { tone: 'danger', label: 'Archived' },
}

function priorityTone(p) {
  if (p === 'urgent' || p === 'high') return 'danger'
  if (p === 'medium') return 'info'
  return ''
}

const OPEN_STATUSES = ['pending', 'in_progress', 'in-progress', 'open', 'reviewing']

// ── Small presentational pieces ─────────────────────────────────────────────────
function CDStat({ label, value, sub, icon, tone }) {
  return (
    <div className="card card-pad-sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span className={`icon-tile ${tone || ''}`} style={{ width: 28, height: 28 }}>{React.cloneElement(icon, { size: 15 })}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function CDInfoRow({ icon, label, value, mono, last }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 13, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        <div className="truncate" style={{ fontSize: 13, color: 'var(--text-soft)', marginTop: 2, fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{value}</div>
      </div>
    </div>
  )
}

const ClientDetail = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()
  const [menuOpen, setMenuOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const menuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  const { data: client, isLoading, error } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { enabled: !!clientId, retry: 1 }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  const { data: billingData } = useQuery(
    ['client-billing', clientId],
    () => apiService.get(`/billing/client/${clientId}`).then(res => res.data),
    { enabled: !!clientId, retry: 1 }
  )

  const { data: sites = [] } = useQuery(
    ['client-sites', clientId],
    () => apiService.get('/sites').then(res => (res.data?.sites || res.data || []).filter(s => s.client_id === clientId)),
    { enabled: !!clientId }
  )

  const { data: requests = [] } = useQuery(
    ['client-requests', clientId],
    () => apiService.get('/requests').then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.requests || [])
      return list.filter(r => r.client_id === clientId)
    }),
    { enabled: !!clientId }
  )

  const resendInvitationMutation = useMutation(
    () => apiService.post(`/clients/${clientId}/resend-invitation`),
    {
      onSuccess: () => toast.success(`Invitation email resent to ${client?.email}`),
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to resend invitation'),
    }
  )

  const deleteClientMutation = useMutation(
    () => apiService.delete(`/clients/${clientId}`),
    {
      onSuccess: () => {
        toast.success('Client deleted successfully')
        queryClient.invalidateQueries(['clients'])
        navigate('/admin/clients')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete client'),
    }
  )

  // Keep the notes textarea in sync with the persisted value (also after a save refetch)
  useEffect(() => { setNoteDraft(client?.notes || '') }, [client?.notes])

  const saveNotesMutation = useMutation(
    (notes) => apiService.put('/clients/profile', { clientId, notes }),
    {
      onSuccess: () => {
        toast.success('Note saved')
        queryClient.invalidateQueries(['client', clientId])
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save note'),
    }
  )

  const handleDeleteClient = async () => {
    setMenuOpen(false)
    const ok = await nsConfirm({
      tone: 'danger',
      icon: 'user',
      title: `Delete ${client?.company_name}?`,
      message: 'This permanently deletes the client and everything attached to them.',
      bullets: [
        'All associated sites and their content are removed',
        'Backups, media and billing history are erased',
        'This cannot be undone',
      ],
      requireText: client?.company_name,
      confirmLabel: 'Delete client',
    })
    if (ok) deleteClientMutation.mutate()
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0)
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
  const formatMonthYear = (d) => d ? new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short' }) : 'N/A'

  const openRequests = useMemo(
    () => requests.filter(r => OPEN_STATUSES.includes(r.status)),
    [requests]
  )

  // Truthful activity feed derived from the client's real sites
  const activity = useMemo(() => {
    return sites
      .map(s => s.status === 'published' && s.published_at
        ? { icon: <Rocket />, tone: 'success', text: <>Published <strong>{s.name}</strong></>, meta: 'Live release', when: s.published_at }
        : { icon: <FileEdit />, tone: '', text: <>Updated <strong>{s.name}</strong></>, meta: 'Site content', when: s.updated_at })
      .sort((a, b) => new Date(b.when) - new Date(a.when))
      .slice(0, 5)
  }, [sites])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <AlertCircle style={{ width: 64, height: 64, color: 'var(--danger)', margin: '0 auto 16px' }} />
        <h3 className="font-medium" style={{ marginBottom: 8 }}>Client Not Found</h3>
        <p className="text-muted" style={{ marginBottom: 24, fontSize: 13 }}>The client you're looking for doesn't exist or has been deleted.</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/clients')}>Back to Clients</button>
      </div>
    )
  }

  const status = getClientStatus(client)
  const planMeta = plansRaw?.[client.subscription_plan]
  const planName = planMeta?.name || (client.subscription_plan
    ? client.subscription_plan.charAt(0).toUpperCase() + client.subscription_plan.slice(1)
    : 'Basic')
  const planPrice = billingData?.plan?.price ?? planMeta?.price ?? 0
  const quota = client.edit_quota || client.monthly_edit_limit || 50
  const editsUsed = client.edits_used || 0
  const usagePct = Math.min(100, Math.round((editsUsed / quota) * 100))
  const siteCount = client.site_count ?? sites.length
  const liveSites = client.published_sites ?? sites.filter(s => s.status === 'published').length
  const industryLabel = INDUSTRIES[client.industry]?.label || 'General Business'

  return (
    <>
      {/* breadcrumb sub-header */}
      <div className="cd-bar">
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('/admin/clients')}>
          <ChevronLeft style={{ width: 15, height: 15 }} /> Clients
        </button>
        <span className="vdiv" style={{ height: 20 }} />
        <span className="cd-crumb">{client.company_name}</span>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* profile header */}
      <div className="card card-pad cd-profile">
        <div className={`avatar avatar-grad-${gradIndex(client.id)}`} style={{ width: 64, height: 64, fontSize: 24, borderRadius: 18, flexShrink: 0 }}>
          {getInitials(client.company_name || client.user_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.025em' }}>{client.company_name}</h1>
            <span className={`badge${STATUS_TONE[status] ? ' badge-' + STATUS_TONE[status] : ''}`}><span className="badge-dot" /> {STATUS_LABEL[status]}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, color: 'var(--text-muted)', fontSize: 13, flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><User style={{ width: 14, height: 14 }} /> {client.user_name}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare style={{ width: 14, height: 14 }} /> {client.email}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Zap style={{ width: 14, height: 14 }} /> {planName} · {formatCurrency(planPrice)}/mo</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          <button className="btn" onClick={() => navigate(`/admin/requests?newFor=${client.id}`)}><Send style={{ width: 15, height: 15 }} /> Message</button>
          <button className="btn" onClick={() => navigate(`/admin/clients/${clientId}/edit`)}><Edit style={{ width: 15, height: 15 }} /> Edit</button>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button className="site-action" title="More actions" onClick={() => setMenuOpen(o => !o)}><MoreHorizontal style={{ width: 16, height: 16 }} /></button>
            {menuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 30,
                background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 12,
                boxShadow: '0 16px 40px rgba(0,0,0,.28)', padding: 6, minWidth: 200,
              }}>
                <button className="menu-item" onClick={() => { setMenuOpen(false); resendInvitationMutation.mutate() }} disabled={resendInvitationMutation.isLoading}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 10px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)', fontSize: 13, textAlign: 'left' }}>
                  <RefreshCw style={{ width: 15, height: 15 }} /> Resend invitation
                </button>
                <button className="menu-item" onClick={handleDeleteClient} disabled={deleteClientMutation.isLoading}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 10px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 13, textAlign: 'left' }}>
                  <Trash2 style={{ width: 15, height: 15 }} /> Delete client
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* key stats */}
      <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <CDStat label="Monthly revenue" value={formatCurrency(planPrice)} sub="on plan" icon={<CreditCard />} tone="accent" />
        <CDStat label="Sites" value={siteCount} sub={`${liveSites} live`} icon={<Globe />} tone="info" />
        <CDStat label="Total edits" value={editsUsed} sub="all time" icon={<FileEdit />} tone="success" />
        <CDStat label="Member since" value={formatMonthYear(client.created_at)} sub={`Last seen ${client.last_login ? formatRelativeTime(client.last_login) : 'never'}`} icon={<Clock />} tone="warning" />
      </div>

      {/* two-column body */}
      <div className="cd-grid">
        {/* main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {/* sites */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Sites</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 3 }}>{sites.length} site{sites.length === 1 ? '' : 's'} · {liveSites} live</div>
              </div>
              <button className="btn btn-sm" onClick={() => navigate(`/admin/sites?clientId=${client.id}`)}><ExternalLink style={{ width: 13, height: 13 }} /> View all</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sites.map(s => {
                const st = SITE_STATUS[s.status] || SITE_STATUS.draft
                return (
                  <div key={s.id} className="cd-site-row" onClick={() => navigate(`/admin/sites/${s.id}`)}>
                    <span className={`avatar avatar-grad-${gradIndex(s.id)}`} style={{ width: 36, height: 36, fontSize: 13, borderRadius: 10 }}>
                      {(s.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="truncate" style={{ fontSize: 13.5, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>— pages · updated {s.updated_at ? formatRelativeTime(s.updated_at) : '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 500 }}>—</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>visits/mo</div>
                    </div>
                    <span className={`badge${st.tone ? ' badge-' + st.tone : ''}`} style={{ flexShrink: 0 }}>{st.tone === 'success' && <span className="badge-dot" />}{st.label}</span>
                    <span className="cd-site-go"><ArrowRight style={{ width: 16, height: 16 }} /></span>
                  </div>
                )
              })}
              {sites.length === 0 && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>No sites yet for this client.</div>}
            </div>
          </div>

          {/* recent activity */}
          <div className="card card-pad">
            <h2 className="h2" style={{ marginBottom: 6 }}>Recent activity</h2>
            <div>
              {activity.map((a, i) => (
                <div key={i} className="activity-row">
                  <span className="activity-tail" />
                  <span className={`icon-tile ${a.tone}`} style={{ width: 30, height: 30, flexShrink: 0, zIndex: 1 }}>{React.cloneElement(a.icon, { size: 15 })}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{a.text}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 2 }}>{a.meta}</div>
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{a.when ? formatRelativeTime(a.when) : ''}</span>
                </div>
              ))}
              {activity.length === 0 && <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>No recent activity.</div>}
            </div>
          </div>
        </div>

        {/* sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* open requests */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h2 className="h2">Open requests</h2>
              {openRequests.length > 0 && <span className="badge badge-accent">{openRequests.length}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {openRequests.map(r => (
                <button key={r.id} className="cd-req-row" onClick={() => navigate(`/admin/requests?open=${r.id}`)}>
                  <span className={`icon-tile ${priorityTone(r.priority)}`} style={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}><MessageSquare style={{ width: 13, height: 13 }} /></span>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div className="truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>{r.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, textTransform: 'capitalize' }}>{r.priority || 'normal'} · {r.created_at ? formatRelativeTime(r.created_at) : ''}</div>
                  </div>
                </button>
              ))}
              {openRequests.length === 0 && (
                <div style={{ padding: '18px 8px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-faint)' }}>
                  <Check style={{ width: 18, height: 18, color: 'var(--success)' }} /><div style={{ marginTop: 6 }}>No open requests</div>
                </div>
              )}
            </div>
          </div>

          {/* plan & billing */}
          <div className="card card-pad">
            <h2 className="h2" style={{ marginBottom: 14 }}>Plan &amp; billing</h2>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>{planName}</span>
              <span className="badge badge-accent">{formatCurrency(planPrice)}/mo</span>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 7 }}>
                <span style={{ color: 'var(--text-soft)' }}>Edits this cycle</span>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{editsUsed} / {quota}</span>
              </div>
              <div className="progress" style={{ height: 7 }}><div style={{ width: `${usagePct}%`, background: usagePct > 90 ? 'var(--warning)' : 'var(--accent)' }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 14 }}>
              <span style={{ color: 'var(--text-soft)' }}>Next billing date</span>
              <span style={{ color: 'var(--text-muted)' }}>{billingData?.nextBillingDate ? formatDate(billingData.nextBillingDate) : 'N/A'}</span>
            </div>
            <button className="btn btn-sm" style={{ width: '100%', marginTop: 16 }} onClick={() => navigate('/admin/billing', { state: { clientId: client.id } })}><CreditCard style={{ width: 13, height: 13 }} /> View invoices</button>
          </div>

          {/* contact */}
          <div className="card card-pad">
            <h2 className="h2" style={{ marginBottom: 6 }}>Contact</h2>
            <CDInfoRow icon={<User style={{ width: 14, height: 14 }} />} label="Primary contact" value={client.user_name} />
            <CDInfoRow icon={<MessageSquare style={{ width: 14, height: 14 }} />} label="Email" value={client.email} mono />
            <CDInfoRow icon={<Globe style={{ width: 14, height: 14 }} />} label="Industry" value={industryLabel} />
            <CDInfoRow icon={<Clock style={{ width: 14, height: 14 }} />} label="Client since" value={formatDate(client.created_at)} last />
          </div>

          {/* internal notes */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h2 className="h2">Internal notes</h2>
              {noteDraft !== (client.notes || '') && (
                <button className="btn btn-sm btn-primary" onClick={() => saveNotesMutation.mutate(noteDraft)} disabled={saveNotesMutation.isLoading}>
                  {saveNotesMutation.isLoading ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
            <textarea className="input" rows={3} value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
              style={{ resize: 'vertical', lineHeight: 1.6, fontSize: 12.5 }}
              placeholder="Add a private note about this client…" />
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>Only your team can see this.</div>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}

export default ClientDetail
